<template>
  <div class="container-fluid py-4">
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="rotate-cw" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <div>
            <h3 class="mb-0 fw-bold">My Review Cycles</h3>
          </div>
        </div>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item"><router-link to="/dashboard">Dashboard</router-link></li>
            <li class="breadcrumb-item active" aria-current="page">My Review Cycles</li>
          </ol>
        </nav>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Review Cycles -->
    <div v-else>
      <div v-if="filteredCycles.length === 0" class="card">
        <div class="card-body text-center py-5">
          <i class="icon-feather-inbox text-muted mb-3" style="font-size: 3rem;"></i>
          <h5 class="text-muted">No review cycles available</h5>
          <p class="text-muted">There are no active or closed review cycles at the moment.</p>
        </div>
      </div>

      <div v-else class="row g-4">
        <div v-for="cycle in filteredCycles" :key="cycle.id" class="col-lg-6 col-xl-4">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <h5 class="card-title mb-0">{{ cycle.review_cycle.name }}</h5>
                <span
                  class="badge p-2"
                  :class="{
                    'bg-success': cycle.review_cycle.status === 'active',
                    'bg-secondary': cycle.review_cycle.status === 'closed',
                  }"
                >
                  {{ cycle.review_cycle.status.replace('_', ' ') }}
                </span>
              </div>

              <p class="card-text text-muted mb-3">
                {{ cycle.review_cycle.description || 'No description provided' }}
              </p>
              <div class="mb-3">
                <small class="text-muted d-block">
                  <i class="bi bi-repeat me-1"></i>
                  Stage: <span
                    class="badge"
                    :class="{
                    'bg-warning': cycle.status === 'pending',
                    'bg-info': cycle.status === 'self_review',
                    'bg-dark': cycle.status === 'manager_review',
                    'bg-primary': cycle.status === 'calibration',
                    'bg-success': cycle.status === 'completed',
                    'bg-secondary': cycle.status === 'disputed'
                  }"
                >
                  {{ cycle.status.replace('_', ' ') }}
                </span>
                </small>
                <small class="text-muted d-block">
                  <i class="bi bi-calendar me-1"></i>
                  Start: {{ formatDate(cycle.review_cycle.start_date) }}
                </small>
                <small class="text-muted d-block">
                  <i class="bi bi-calendar me-1"></i>
                  End: {{ formatDate(cycle.review_cycle.end_date) }}
                </small>
                <small v-if="cycle.review_cycle.self_review_deadline" class="text-muted d-block">
                  <i class="bi bi-clock me-1"></i>
                  Self Review Due: {{ formatDate(cycle.review_cycle.self_review_deadline) }}
                </small>
              </div>

              <div class="mt-auto">
                <button
                  v-if="cycle.review_cycle.status === 'active' && cycle.status === 'pending'"
                  @click="startReview(cycle)"
                  class="btn btn-primary w-100"
                  :disabled="submitting"
                >
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Start Review
                </button>
                <button
                  v-if="cycle.status !== 'pending'"
                  @click="$router.push(`/performance/review/${cycle.id}`)"
                  class="btn btn-primary w-100"
                >
                  View Review
                </button>
                <div v-if="cycle.review_cycle.status === 'closed' && cycle.status !== 'completed'" class="text-muted text-center">
                  <small>Review cycle has ended</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Manager Selection Modal -->
    <ManagerSelectionModal
      :open="showModal"
      :cycle-name="selectedCycle?.name || ''"
      :managers="managers"
      :saving="submitting"
      @close="closeModal"
      @save="updatePerformanceReview"
    />
  </div>
</template>

<script>
import { usePerformanceStore } from '@/stores/performance';
import { mapState } from 'pinia';
import userService from '@/api/userService';
import ManagerSelectionModal from '@/components/modals/ManagerSelectionModal.vue';
import {decrypt} from "@/utils/crypto";
import notify from '@/utils/notify';

export default {
  name: "MyReviews",
  components: {
    ManagerSelectionModal
  },

  data() {
    return {
      loading: false,
      submitting: false,
      selectedCycle: null,
      managers: [], // Will be populated with manager list
      showModal: false
    };
  },

  computed: {
    ...mapState(usePerformanceStore, ['reviewCycles']),

    filteredCycles() {
      if (!Array.isArray(this.reviewCycles)) {
        return [];
      }
      return this.reviewCycles.filter(cycle =>
        cycle.review_cycle &&
        (cycle.review_cycle.status === 'active' || cycle.review_cycle.status === 'closed')
      );
    }
  },

  async mounted() {
    await this.fetchData();
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const performanceStore = usePerformanceStore();
        const employeeId = this.getCurrentUserId();
        await performanceStore.fetchReviewCycles(employeeId);
        await this.fetchManagers();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchManagers() {
      try {
        this.managers = await userService.getUsers();
      } catch (error) {
        console.error('Error fetching managers:', error);
        // Fallback to empty array
        this.managers = [];
      }
    },

    startReview(cycle) {
      this.selectedCycle = cycle;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedCycle = null;
    },

    async updatePerformanceReview(selectedManagerId) {
      this.submitting = true;
      try {
        const performanceStore = usePerformanceStore();

        await performanceStore.updateReview(this.selectedCycle.id, {
          reviewer_id: selectedManagerId,
          status: 'self_review'
        });

        this.$router.push(`/performance/review/${this.selectedCycle.id}`);
      } catch (error) {
        console.error('Error updating performance review:', error);
        notify.alert.error(error.response?.data?.message || 'Failed to update review manager');
      } finally {
        this.submitting = false;
      }
    },

    getCurrentUserId() {
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
      return this.profile.employee_id;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.page-title {
  color: #2d3748;
  font-weight: 600;
}

.card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.badge {
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.icon-box {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-1px);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
